const MAX_ENTRIES = 32;
const PREVIEW_TTL_MS = 30_000;

/** Page-scoped only: never persist author previews globally or in browser storage. */
export function createPreviewCache() {
    type Entry = { promise: Promise<unknown>; value?: unknown; expires: number };
    const entries = new Map<string, Entry>();
    const current = (key: string) => {
        const entry = entries.get(key);
        if (entry && entry.expires > Date.now()) return entry;
        entries.delete(key);
    };
    return {
        peek<T>(key: string): T | undefined { return current(key)?.value as T | undefined; },
        load<T>(key: string, load: () => Promise<T>): Promise<T> {
            const cached = current(key);
            if (cached) return cached.promise as Promise<T>;
            if (entries.size >= MAX_ENTRIES) entries.delete(entries.keys().next().value!);
            const entry: Entry = { promise: Promise.resolve(), expires: Date.now() + PREVIEW_TTL_MS };
            entries.set(key, entry);
            entry.promise = (async () => {
                try {
                    const value = await load();
                    if (entries.get(key) === entry) {
                        entry.value = value;
                        entry.expires = Date.now() + PREVIEW_TTL_MS;
                    }
                    return value;
                } catch (error) {
                    if (entries.get(key) === entry) entries.delete(key);
                    throw error;
                }
            })();
            return entry.promise as Promise<T>;
        },
        clear() { entries.clear(); },
    };
}
